// React.js
import React, { FC, useEffect, useState } from 'react'
// Types
import type { ExplicitCommerceWishlist } from '@framework/types'
import type { Product, ProductVariant } from '@commerce/types/product'
import type { YadavDiamond } from '@commerce/types/diamond'
// Hooks
import useWishlist from '@framework/wishlist/use-wishlist'
import useAddWishlistItem from '@framework/wishlist/use-add-item'
import useRemoveWishlistItem from '@framework/wishlist/use-remove-item'
// Icons
import { Heart } from '@components/icons'
// Utils
import cn from 'clsx'
import { gtagEvent } from '@utils/gtag-events'
// Styles
import s from './WishlistButton.module.css'

type Props = {
  className?: string
  productId?: Product['id']
  variantId?: ProductVariant['id']
  diamondId?: YadavDiamond['id']
  isCart?: boolean
  isProductCard?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

const WishlistButton: FC<Props> = ({
  className,
  productId,
  variantId,
  diamondId,
  isCart,
  isProductCard,
  ...props
}) => {
  const { data: wishlist }: { data?: ExplicitCommerceWishlist | null } =
    useWishlist()
  const addItem = useAddWishlistItem()
  const removeItem = useRemoveWishlistItem()
  const [loading, setLoading] = useState(false)
  const [inWishList, setInWishList] = useState(false)
  const [itemInWishlist, setItemInWishlist] =
    useState<ExplicitCommerceWishlist['items'][0]>()

  // Check if a product, a diamond or a diamond + setting combination are already in the wishlist
  useEffect(() => {
    if (wishlist) {
      if (variantId && productId && diamondId) {
        const item = wishlist.items.find(
          (item) =>
            item.product_id === Number(productId) &&
            item.variant_id === Number(variantId) &&
            item.diamond_id === Number(diamondId)
        )
        setItemInWishlist(item)
        if (item) {
          setInWishList(true)
        } else {
          setInWishList(false)
        }
      } else if (productId && variantId && !diamondId) {
        const item = wishlist.items.find(
          (item) =>
            item.product_id === Number(productId) &&
            item.variant_id === Number(variantId) &&
            item.diamond_id === null
        )
        setItemInWishlist(item)
        if (item) {
          setInWishList(true)
        } else {
          setInWishList(false)
        }
      } else if (diamondId && !productId && !variantId) {
        const item = wishlist.items.find(
          (item) =>
            item.diamond_id === Number(diamondId) &&
            item.product_id === null &&
            item.variant_id === null
        )
        setItemInWishlist(item)
        if (item) {
          setInWishList(true)
        } else {
          setInWishList(false)
        }
      } else {
        setInWishList(false)
      }
    } else {
      setInWishList(false)
    }
  }, [diamondId, productId, variantId, wishlist])

  const handleWishlistChange = async (
    e: React.MouseEvent<HTMLButtonElement>,
    productId?: Product['id'],
    variantId?: ProductVariant['id'],
    diamondId?: YadavDiamond['id']
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (loading) return

    setLoading(true)

    try {
      if (inWishList && itemInWishlist) {
        setInWishList(false)
        await removeItem({ id: itemInWishlist.id })
      } else {
        if (variantId && diamondId) {
          await addItem({
            variantId: variantId,
            diamondId: diamondId,
          })
          setInWishList(true)
          gtagEvent(
            'add_to_wishlist',
            itemInWishlist?.variant,
            itemInWishlist?.diamond
          )
        } else if (variantId && !diamondId) {
          await addItem({
            variantId: variantId,
          })
          setInWishList(true)
          gtagEvent('add_to_wishlist', itemInWishlist?.variant, null)
        } else if (diamondId && !variantId) {
          await addItem({
            diamondId: diamondId,
          })
          setInWishList(true)
          gtagEvent('add_to_wishlist', null, itemInWishlist?.diamond)
        }
      }
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  // if (!wishlist) {
  //   return isCart ? (
  //     <Skeleton className="w-28 !min-h-[16px] mt-1.5" />
  //   ) : (
  //     <Skeleton className="w-3 h-2 mr-4 rounded-full" />
  //   )
  // }

  return (
    <button
      aria-label="Add to wishlist"
      className={cn(
        s.root,
        className,
        isProductCard ? s.root1 : '',
        isCart ? 'mr-0' : ''
      )}
      onClick={(e) => {
        handleWishlistChange(e, productId, variantId, diamondId)
      }}
      {...props}
    >
      <Heart
        className={cn(s.icon, {
          [s.loading]: loading,
          [s.inWishlist]: inWishList,
          [s.icon1]: isProductCard,
          [s.icon2]: isCart,
        })}
        isProductCard={isProductCard || false}
      />
      {isCart && (
        <span className="font-normal text-gray-dark sm:pl-1 pl-[2px] whitespace-nowrap text-xs hover:text-accent-9">
          {`${inWishList ? 'In Wishlist' : 'Move To Wishlist'}`}
        </span>
      )}
    </button>
  )
}

export default WishlistButton
